import { useEffect, useMemo } from "react";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import {
  PARTNER_SPOTLIGHT_ENTRIES,
  type PartnerSpotlightEntry,
  partnerSpotlightLogoSrc,
} from "config/partnerSpotlightLogos";

const SLIDE_GAP_PX = 24;
/** With `loop`, flex `gap` does not separate the last slide from the cloned first; pad ends so the seam matches `SLIDE_GAP_PX`. */
const SLIDE_LOOP_END_PAD_PX = SLIDE_GAP_PX / 2;
/** Fixed band so wide vs tall SVGs occupy the same footprint (object-fit scales inside). */
const LOGO_BAND_HEIGHT_PX = 56;
/** Fixed row height: band + constant vertical breathing room (unchanged from pre-scale layout). */
const SLIDE_ROW_MIN_HEIGHT_PX = LOGO_BAND_HEIGHT_PX + 20;

/** Slower advance; avoid permanent stop on scroll/focus (felt like random stops). */
const PARTNER_AUTOPLAY_DELAY_MS = 5200;

type PartnerSpotlightCarouselProps = {
  entries?: PartnerSpotlightEntry[];
};

export default function PartnerSpotlightCarousel({
  entries = PARTNER_SPOTLIGHT_ENTRIES,
}: PartnerSpotlightCarouselProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));

  const slidesInView = useMemo(() => {
    if (isXs) return 2;
    if (isMd) return 3;
    if (isLg) return 4;
    return 6;
  }, [isXs, isMd, isLg]);

  const slideBasisCss = useMemo(
    () =>
      `calc((100% - ${(slidesInView - 1) * SLIDE_GAP_PX}px) / ${slidesInView})`,
    [slidesInView],
  );

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: PARTNER_AUTOPLAY_DELAY_MS,
        /** Page scroll / stray pointer must not kill the loop for the session */
        stopOnInteraction: false,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
        playOnInit: true,
      }),
    [],
  );

  const carouselAlign = useMemo(() => ({ align: "start" as const }), []);

  const canLoop = entries.length > slidesInView;

  const [viewportRef, emblaApi] = useEmblaCarousel(
    { ...carouselAlign, loop: canLoop },
    entries.length ? [autoplayPlugin] : [],
  );

  useEffect(() => {
    if (!entries.length || !emblaApi) return;
    const loopEnabled = entries.length > slidesInView;
    emblaApi.reInit({
      ...carouselAlign,
      loop: loopEnabled,
    });
    queueMicrotask(() => {
      autoplayPlugin.reset();
    });
  }, [
    emblaApi,
    carouselAlign,
    entries.length,
    slidesInView,
    autoplayPlugin,
  ]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: { xs: 2, sm: 2.5 },
        position: "relative",
        width: "100%",
        minWidth: 0,
        alignSelf: "stretch",
      }}
    >
      <Box
        sx={{ overflow: "hidden", width: "100%", px: { xs: 0, md: 0.25 } }}
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label="Partner logos"
      >
        <Box
          sx={{
            display: "flex",
            gap: `${SLIDE_GAP_PX}px`,
            touchAction: "pan-y pinch-zoom",
          }}
        >
          {entries.map((entry, index) => {
            const scale = entry.logoDisplayScale ?? 1;
            const isFirst = index === 0;
            const isLast = index === entries.length - 1;
            return (
              <Box
                aria-roledescription="slide"
                key={entry.id}
                sx={{
                  flex: `0 0 ${slideBasisCss}`,
                  minWidth: 0,
                  flexShrink: 0,
                  boxSizing: "border-box",
                  minHeight: SLIDE_ROW_MIN_HEIGHT_PX,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1,
                  ...(isFirst && {
                    pl: `calc(${theme.spacing(1)} + ${SLIDE_LOOP_END_PAD_PX}px)`,
                  }),
                  ...(isLast && {
                    pr: `calc(${theme.spacing(1)} + ${SLIDE_LOOP_END_PAD_PX}px)`,
                  }),
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: `${LOGO_BAND_HEIGHT_PX}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <Box
                    component="img"
                    src={partnerSpotlightLogoSrc(entry.file)}
                    alt={entry.alt}
                    loading="eager"
                    draggable={false}
                    sx={{
                      display: "block",
                      width: "100%",
                      maxWidth: "100%",
                      height: `${LOGO_BAND_HEIGHT_PX}px`,
                      maxHeight: `${LOGO_BAND_HEIGHT_PX}px`,
                      objectFit: "contain",
                      objectPosition: "center",
                      transform:
                        scale !== 1 ? `scale(${scale})` : undefined,
                      transformOrigin: "center center",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
